// Gift Wrap handling for the cart drawer / cart page:
// - Guard: Gift Wrap can't be the only product in the cart. Whenever the cart
//   changes (or on page load if the server-rendered cart is Gift Wrap only),
//   silently remove it and re-render the cart drawer / cart page.
// - <gift-wrap-checkbox> (snippets/cart-gift-wrap.liquid): checked adds one gift
//   wrap, unchecked removes it. The server-rendered state refreshes whenever the
//   cart drawer re-renders; sync() covers cart updates, drawer open and the cart
// While any cart update or gift wrap check is in flight, the checkout buttons
// show a loader and are disabled, so a Gift Wrap-only cart can't reach checkout.
(() => {
    const { handle: GIFT_WRAP_HANDLE, isAlone } = window.giftWrap || {};
    if (!GIFT_WRAP_HANDLE) return;

    const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    const CHECK_DELAY = 300;
    const nativeFetch = window.fetch.bind(window);
    let checkTimer;
    let isChecking = false;
    let pendingCartRequests = 0;
    let pendingGiftWrapUpdates = 0;

    const CHECKOUT_BUTTONS = '.wt-cart__cta[name="checkout"]';

    function isBusy() {
        return (
            pendingCartRequests > 0 ||
            pendingGiftWrapUpdates > 0 ||
            isChecking ||
            checkTimer !== undefined
        );
    }

    // Re-run after every render, since the cart drawer / cart page replace the button.
    function updateBusyState() {
        const busy = isBusy();
        document.documentElement.classList.toggle("gift-wrap-busy", busy);
        document.querySelectorAll(CHECKOUT_BUTTONS).forEach((button) => {
            if (button.dataset.checkoutProcessing === "true") return;
            button.classList.toggle("loading", busy);
            button.toggleAttribute("aria-busy", busy);
            if (busy) {
                // Leave buttons the server rendered disabled (empty cart) disabled.
                if (!button.disabled) button.dataset.giftWrapDisabled = "";
                button.disabled = true;
            } else if ("giftWrapDisabled" in button.dataset) {
                delete button.dataset.giftWrapDisabled;
                button.disabled = false;
            }
            button
                .querySelector(".loading-overlay__spinner")
                ?.classList.toggle("hidden", !busy);
        });
    }

    // Capture phase, ahead of the drawer's own checkout click handler.
    document.addEventListener(
        "click",
        (event) => {
            if (isBusy() && event.target.closest(CHECKOUT_BUTTONS)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        },
        true,
    );
    document.addEventListener(
        "submit",
        (event) => {
            if (isBusy() && event.submitter?.matches(CHECKOUT_BUTTONS)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        },
        true,
    );

    // Debounced so the theme finishes rendering its own add/change response first.
    function fetchCart() {
        return nativeFetch(`${routes.cart_url}.js`).then((res) => res.json());
    }

    function scheduleCheck() {
        clearTimeout(checkTimer);
        checkTimer = setTimeout(() => {
            checkTimer = undefined;
            removeLoneGiftWrap();
        }, CHECK_DELAY);
        updateBusyState();
    }

    function getCartRenderer() {
        const cartItems = document.querySelector("cart-items");
        if (cartItems && document.getElementById("main-cart-items")) {
            const sections = cartItems.getSectionsToRender();
            return {
                sections: sections.map((section) => section.section),
                render(state) {
                    const isEmpty = state.item_count === 0;
                    cartItems.classList.toggle("is-empty", isEmpty);
                    document
                        .getElementById("main-cart-footer")
                        ?.classList.toggle("is-empty", isEmpty);

                    sections.forEach((section) => {
                        const container = document.getElementById(section.id);
                        if (!container) return;
                        const target =
                            container.querySelector(section.selector) || container;
                        target.innerHTML =
                            cartItems.getSectionInnerHTML(
                                state.sections[section.section],
                                section.selector,
                            ) || "";
                    });
                },
            };
        }

        const drawer = document.querySelector("cart-drawer");
        if (drawer) {
            return {
                sections: drawer.getSectionsToRender().map((section) => section.id),
                render(state) {
                    drawer.renderContents(state, false);
                    drawer.classList.toggle("wt-cart--empty", state.item_count === 0);
                },
            };
        }

        return null;
    }

    async function removeLoneGiftWrap() {
        // Another add/change is still in flight; its completion reschedules the check.
        if (pendingCartRequests > 0) return updateBusyState();
        if (isChecking) return scheduleCheck();
        isChecking = true;
        updateBusyState();

        try {
            const cart = await fetchCart();
            const giftWrapLines = cart.items.filter(
                (item) => item.handle === GIFT_WRAP_HANDLE,
            );
            if (!giftWrapLines.length || giftWrapLines.length !== cart.items.length)
                return;

            const renderer = getCartRenderer();
            let state;

            for (const [index, item] of giftWrapLines.entries()) {
                const body = { id: item.key, quantity: 0 };
                if (renderer && index === giftWrapLines.length - 1) {
                    body.sections = renderer.sections;
                    body.sections_url = window.location.pathname;
                }

                state = await nativeFetch(`${routes.cart_change_url}.js`, {
                    ...fetchConfig(),
                    body: JSON.stringify(body),
                }).then((res) => res.json());
            }

            if (renderer && state?.sections) renderer.render(state);
        } catch (e) {
            console.error(e);
        } finally {
            isChecking = false;
            updateBusyState();
        }
    }

    // Watch every AJAX cart mutation (theme code, fragrance finder, apps), since
    // not all of them publish PUB_SUB_EVENTS.cartUpdate.
    function onCartRequestDone() {
        pendingCartRequests = Math.max(0, pendingCartRequests - 1);
        scheduleCheck();
    }

    window.fetch = function (...args) {
        const request = nativeFetch(...args);
        const url = args[0] instanceof Request ? args[0].url : String(args[0]);
        if (CART_MUTATION_URL.test(url)) {
            pendingCartRequests++;
            updateBusyState();
            request.then(onCartRequestDone, onCartRequestDone);
        }
        return request;
    };

    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        if (CART_MUTATION_URL.test(String(url))) {
            pendingCartRequests++;
            updateBusyState();
            this.addEventListener("loadend", onCartRequestDone, { once: true });
        }
        return nativeOpen.call(this, method, url, ...rest);
    };

    document.addEventListener("DOMContentLoaded", () => {
        if (typeof subscribe === "function")
            subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleCheck);
    });

    if (isAlone) scheduleCheck();

    if (customElements.get("gift-wrap-checkbox")) return;
    customElements.define(
        "gift-wrap-checkbox",
        class GiftWrapCheckbox extends HTMLElement {
            connectedCallback() {
                this.input = this.querySelector("input");
                this.input.addEventListener("change", this.onChange);
                document.addEventListener("click", this.onDocumentClick);
                if (typeof subscribe === "function")
                    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.sync);
            }

            disconnectedCallback() {
                document.removeEventListener("click", this.onDocumentClick);
                this.unsubscribe?.();
            }

            onDocumentClick = (event) => {
                if (event.target.closest(".wt-cart__trigger")) this.sync();
            };

            apply(cart) {
                const hasGiftWrap = cart.items.some(
                    (item) => item.handle === GIFT_WRAP_HANDLE,
                );
                const otherCount = cart.items
                    .filter((item) => item.handle !== GIFT_WRAP_HANDLE)
                    .reduce((sum, item) => sum + item.quantity, 0);
                this.input.checked = hasGiftWrap;
                this.hidden = otherCount === 0;
            }

            sync = async () => {
                try {
                    this.apply(await fetchCart());
                } catch (e) {
                    console.error(e);
                }
            };

            onChange = async () => {
                const checked = this.input.checked;
                this.input.disabled = true;
                pendingGiftWrapUpdates++;
                updateBusyState();

                try {
                    const cart = await fetchCart();
                    const line = cart.items.find(
                        (item) => item.handle === GIFT_WRAP_HANDLE,
                    );
                    let url;
                    let body;
                    if (checked && !line) {
                        url = `${routes.cart_add_url}.js`;
                        body = {
                            items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
                        };
                    } else if (!checked && line) {
                        url = `${routes.cart_change_url}.js`;
                        body = { id: line.key, quantity: 0 };
                    } else {
                        return this.apply(cart);
                    }

                    const renderer = getCartRenderer();
                    if (renderer) {
                        body.sections = renderer.sections;
                        body.sections_url = window.location.pathname;
                    }

                    // Patched fetch, so the guard re-checks after this mutation.
                    const state = await fetch(url, {
                        ...fetchConfig(),
                        body: JSON.stringify(body),
                    }).then((res) => res.json());
                    if (state.status) throw new Error(state.description);

                    if (renderer && state.sections) renderer.render(state);
                    if (this.isConnected) this.sync();
                } catch (e) {
                    console.error(e);
                    this.sync();
                } finally {
                    this.input.disabled = false;
                    pendingGiftWrapUpdates--;
                    updateBusyState();
                }
            };
        },
    );
})();